import React, { useState, useEffect } from 'react';
import { X, Gift, Check, Lock, Coins, Loader2, RefreshCw, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '../lib/utils';
import { useSettings } from './SettingsContext';

export interface DailyChallenge {
  id: string;
  name: string;
  description: string;
  tier: 'easy' | 'medium' | 'hard';
  goal: number;
  progress: number;
  coinReward: number;
  claimed: boolean;
}

interface DailyChallengesProps {
  isOpen: boolean;
  onClose: () => void;
  uid: string;
  onClaimReward?: (challengeId: string, coinReward: number) => void;
}

const CHALLENGES_CONFIG: Omit<DailyChallenge, 'progress' | 'claimed'>[] = [
  {
    id: 'win_1_game',
    name: 'Prima Vittoria',
    description: 'Vinci una partita',
    tier: 'easy',
    goal: 1,
    coinReward: 10,
  },
  {
    id: 'win_3_games',
    name: 'Inarrestabile',
    description: 'Vinci 3 partite',
    tier: 'medium',
    goal: 3,
    coinReward: 30,
  },
  {
    id: 'score_10_points',
    name: 'Mano Calda',
    description: 'Totalizza 10 punti in una singola mano',
    tier: 'hard',
    goal: 1,
    coinReward: 50,
  },
];

function getTodayKey(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function getTierBadgeStyles(tier: 'easy' | 'medium' | 'hard') {
  switch (tier) {
    case 'easy':
      return {
        bg: 'bg-green-500/20',
        border: 'border-green-500/30',
        text: 'text-green-400',
        label: 'Facile',
        dot: 'bg-green-400',
      };
    case 'medium':
      return {
        bg: 'bg-yellow-500/20',
        border: 'border-yellow-500/30',
        text: 'text-yellow-400',
        label: 'Medio',
        dot: 'bg-yellow-400',
      };
    case 'hard':
      return {
        bg: 'bg-red-500/20',
        border: 'border-red-500/30',
        text: 'text-red-400',
        label: 'Difficile',
        dot: 'bg-red-400',
      };
  }
}

function getTierProgressColor(tier: 'easy' | 'medium' | 'hard'): string {
  switch (tier) {
    case 'easy': return 'bg-green-400';
    case 'medium': return 'bg-yellow-400';
    case 'hard': return 'bg-red-400';
  }
}

export default function DailyChallenges({
  isOpen,
  onClose,
  uid,
  onClaimReward,
}: DailyChallengesProps) {
  const { playSfx, triggerHaptic } = useSettings();
  const [challenges, setChallenges] = useState<DailyChallenge[]>([]);
  const [loading, setLoading] = useState(true);
  const [claimingId, setClaimingId] = useState<string | null>(null);

  // Load challenges from localStorage (keyed by uid + date)
  const loadChallenges = () => {
    setLoading(true);
    try {
      const stored = localStorage.getItem(`daily_challenges_${uid}`);
      const todayKey = getTodayKey();
      
      if (stored) {
        const data = JSON.parse(stored);
        // Reset if it's a new day
        if (data.date !== todayKey) {
          // Fresh challenges for a new day - all progress reset, but we keep claimed state logic
          const fresh: DailyChallenge[] = CHALLENGES_CONFIG.map(c => ({
            ...c,
            progress: 0,
            claimed: false,
          }));
          localStorage.setItem(`daily_challenges_${uid}`, JSON.stringify({ date: todayKey, challenges: fresh }));
          setChallenges(fresh);
        } else {
          setChallenges(data.challenges);
        }
      } else {
        // First time ever - initialize
        const fresh: DailyChallenge[] = CHALLENGES_CONFIG.map(c => ({
          ...c,
          progress: 0,
          claimed: false,
        }));
        localStorage.setItem(`daily_challenges_${uid}`, JSON.stringify({ date: todayKey, challenges: fresh }));
        setChallenges(fresh);
      }
    } catch {
      // Fallback to defaults
      setChallenges(CHALLENGES_CONFIG.map(c => ({ ...c, progress: 0, claimed: false })));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadChallenges();
    }
  }, [isOpen, uid]);

  const saveChallenges = (updated: DailyChallenge[]) => {
    const todayKey = getTodayKey();
    localStorage.setItem(`daily_challenges_${uid}`, JSON.stringify({ date: todayKey, challenges: updated }));
    setChallenges(updated);
  };

  // Called by parent to update progress externally
  const updateProgress = (challengeId: string, delta: number) => {
    setChallenges(prev => {
      const updated = prev.map(c => {
        if (c.id === challengeId && !c.claimed) {
          return { ...c, progress: Math.min(c.goal, c.progress + delta) };
        }
        return c;
      });
      const todayKey = getTodayKey();
      localStorage.setItem(`daily_challenges_${uid}`, JSON.stringify({ date: todayKey, challenges: updated }));
      return updated;
    });
  };

  const handleClaim = async (challenge: DailyChallenge) => {
    if (challenge.claimed || challenge.progress < challenge.goal) return;
    
    playSfx('settebello');
    triggerHaptic('medium');
    setClaimingId(challenge.id);
    
    // Simulate async claim (e.g. coin animation)
    await new Promise(resolve => setTimeout(resolve, 600));
    
    const updated = challenges.map(c =>
      c.id === challenge.id ? { ...c, claimed: true } : c
    );
    saveChallenges(updated);
    setClaimingId(null);
    
    onClaimReward?.(challenge.id, challenge.coinReward);
  };

  const allClaimed = challenges.length > 0 && challenges.every(c => c.claimed);
  const totalCoins = challenges.reduce((sum, c) => sum + (c.claimed ? c.coinReward : 0), 0);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.95, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, y: 20, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="w-full max-w-md glass-panel rounded-3xl border border-white/10 overflow-hidden shadow-2xl"
          >
            {/* Header */}
            <div className="p-5 pb-4 border-b border-white/5 flex justify-between items-center bg-gradient-to-r from-yellow-500/10 to-amber-500/5">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-yellow-500/20 border border-yellow-500/30 flex items-center justify-center">
                  <Gift className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <h2 className="text-lg font-black uppercase text-yellow-400 tracking-wider" style={{ fontFamily: '"Playfair Display", serif' }}>
                    Sfide Giornaliere
                  </h2>
                  <p className="text-[10px] text-white/40 font-medium">Completale per ricevere monete!</p>
                </div>
              </div>
              <button
                onClick={() => {
                  playSfx('click');
                  triggerHaptic('light');
                  onClose();
                }}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-white/60" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto scrollbar-thin">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-12 space-y-3">
                  <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
                  <p className="text-white/50 text-xs">Caricamento sfide...</p>
                </div>
              ) : (
                <>
                  {challenges.map((challenge, idx) => {
                    const styles = getTierBadgeStyles(challenge.tier);
                    const progressColor = getTierProgressColor(challenge.tier);
                    const isComplete = challenge.progress >= challenge.goal;
                    const canClaim = isComplete && !challenge.claimed;
                    const isClaiming = claimingId === challenge.id;

                    return (
                      <motion.div
                        key={challenge.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.08 }}
                        className={cn(
                          "relative rounded-2xl border overflow-hidden transition-all",
                          isComplete && !challenge.claimed
                            ? styles.border
                            : "border-white/5 bg-white/5"
                        )}
                      >
                        {/* Background accent for completable */}
                        {canClaim && (
                          <motion.div
                            className={cn("absolute inset-0 opacity-10", styles.bg)}
                            animate={{ opacity: [0.05, 0.15, 0.05] }}
                            transition={{ duration: 2, repeat: Infinity }}
                          />
                        )}

                        <div className="p-4 relative">
                          {/* Top row: badge + name + reward */}
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="flex-1 min-w-0">
                              {/* Tier badge */}
                              <div className={cn("inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider mb-2", styles.bg, styles.border, styles.text)}>
                                <span className={cn("w-1.5 h-1.5 rounded-full", styles.dot)} />
                                {styles.label}
                              </div>
                              {/* Challenge name */}
                              <h3 className="text-sm font-bold text-white mb-0.5 leading-tight">{challenge.name}</h3>
                              <p className="text-[10px] text-white/40 leading-tight">{challenge.description}</p>
                            </div>

                            {/* Coin reward badge */}
                            <div className={cn(
                              "flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-black shrink-0",
                              challenge.claimed
                                ? "bg-white/10 text-white/40"
                                : "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30"
                            )}>
                              <Coins className="w-3.5 h-3.5" />
                              {challenge.coinReward}
                            </div>
                          </div>

                          {/* Progress bar */}
                          <div className="mb-3">
                            <div className="flex justify-between items-center mb-1.5">
                              <span className="text-[9px] text-white/40 font-medium uppercase tracking-wider">
                                Progresso
                              </span>
                              <span className="text-[10px] font-bold text-white/60">
                                {challenge.progress} / {challenge.goal}
                              </span>
                            </div>
                            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                              <motion.div
                                className={cn("h-full rounded-full", progressColor)}
                                initial={{ width: 0 }}
                                animate={{ width: `${Math.min(100, (challenge.progress / challenge.goal) * 100)}%` }}
                                transition={{ type: 'spring', damping: 20, stiffness: 100 }}
                              />
                            </div>
                          </div>

                          {/* Claim button */}
                          {challenge.claimed ? (
                            <div className="flex items-center justify-center gap-1.5 py-2 bg-white/5 rounded-xl text-white/40 text-xs font-bold uppercase tracking-wider">
                              <Check className="w-3.5 h-3.5" />
                              Ottenuto
                            </div>
                          ) : canClaim ? (
                            <button
                              onClick={() => handleClaim(challenge)}
                              disabled={isClaiming}
                              className={cn(
                                "w-full py-2 rounded-xl font-black uppercase text-xs tracking-wider transition-all active:scale-95",
                                "bg-yellow-500 hover:bg-yellow-400 text-black shadow-lg shadow-yellow-500/25",
                                isClaiming && "opacity-70 cursor-not-allowed"
                              )}
                            >
                              {isClaiming ? (
                                <span className="flex items-center justify-center gap-2">
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  Riscatta...
                                </span>
                              ) : (
                                "Riscatta Ricompensa"
                              )}
                            </button>
                          ) : (
                            <div className="flex items-center justify-center gap-1.5 py-2 bg-white/5 rounded-xl text-white/30 text-xs font-bold uppercase tracking-wider">
                              <Lock className="w-3.5 h-3.5" />
                              Completa per sbloccare
                            </div>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </>
              )}
            </div>

            {/* Footer summary */}
            {!loading && (
              <div className="px-4 py-3 border-t border-white/5 bg-gradient-to-r from-black/60 to-black/40">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-yellow-400" />
                    <span className="text-xs text-white/60 font-medium">
                      {allClaimed ? 'Tutte le sfide completate!' : `${challenges.filter(c => !c.claimed).length} sfida/e rimasta/e`}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs font-black text-yellow-400">
                    <Coins className="w-4 h-4" />
                    +{totalCoins} oggi
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

// Export a helper to update challenge progress from outside
export function useDailyChallengeUpdater(uid: string) {
  const updateChallenge = (challengeId: string, delta: number) => {
    try {
      const todayKey = getTodayKey();
      const key = `daily_challenges_${uid}`;
      const stored = localStorage.getItem(key);
      if (!stored) return;
      const data = JSON.parse(stored);
      if (data.date !== todayKey) return;
      
      const updated = data.challenges.map((c: DailyChallenge) => {
        if (c.id === challengeId && !c.claimed) {
          return { ...c, progress: Math.min(c.goal, c.progress + delta) };
        }
        return c;
      });
      localStorage.setItem(key, JSON.stringify({ date: todayKey, challenges: updated }));
    } catch {
      // Silently fail
    }
  };

  return { updateChallenge };
}